
import { Component, Vue } from 'vue-property-decorator'
import Empty from '@/components/Empty/index.vue'
import { UserModule } from '@/store/modules/user'
import {
  listPermissions,
  listRoles,
  createRole,
  deleteRole,
  getRolePermissions,
  updateRolePermissions
} from '@/api/permission'

interface RoleRow {
  id?: number
  role: number
  name: string
  code: string
  desc: string
  systemFlag: boolean
}

@Component({
  name: 'RolePermission',
  components: { Empty }
})
export default class extends Vue {
  private roleList: RoleRow[] = []
  private listLoading = false
  private treeData: any[] = []
  private treeLoaded = false
  /** 叶子权限 id（无子节点） */
  private leafIdSet: Set<number> = new Set()
  /** childId -> parentId */
  private parentOf: Record<number, number> = {}
  private dialogVisible = false
  private authLoading = false
  private saving = false
  private authReadonly = false
  private currentRole: RoleRow | null = null

  private createVisible = false
  private creating = false
  private createForm = { name: '', code: '', remark: '' }
  private createRules = {
    name: [{ required: true, message: '请输入角色名称', trigger: 'blur' }],
    code: [
      { required: true, message: '请输入角色标识', trigger: 'blur' },
      {
        pattern: /^[A-Z][A-Z0-9_]{1,31}$/,
        message: '大写字母开头，2～32位字母数字下划线',
        trigger: 'blur'
      }
    ]
  }

  get isSuperAdmin() {
    return UserModule.isSuperAdmin
  }

  get dialogTitle() {
    const name = (this.currentRole && this.currentRole.name) || ''
    return this.authReadonly ? `查看权限 - ${name}` : `授权 - ${name}`
  }

  get readonlyTip() {
    if (this.currentRole && this.currentRole.role === 0) {
      return '超级管理员默认拥有全部权限，不可修改。'
    }
    return '当前账号无权修改该角色权限，仅可查看。'
  }

  created() {
    this.refreshList()
    this.ensureTree()
  }

  private canAuthorize(row: RoleRow) {
    if (!row || row.role === 0) return false
    if (UserModule.isSuperAdmin) {
      // 超管：平台/商家/派送/自定义均可授权
      return true
    }
    // 平台：仅商家管理员
    return row.role === 1 && UserModule.hasPermission('permission:config')
  }

  private async refreshList() {
    this.listLoading = true
    try {
      const res: any = await listRoles()
      if (String(res.data.code) === '1' && Array.isArray(res.data.data)) {
        this.roleList = res.data.data.map((r: any) => ({
          id: r.id,
          role: r.roleValue,
          name: r.name,
          code: r.code,
          desc: r.remark || '',
          systemFlag: Number(r.systemFlag) === 1
        }))
        if (!this.roleList.length) {
          this.$message.warning('暂无角色数据，请先执行 sql/2026-10-26-admin-role-custom.sql')
        }
      } else {
        this.roleList = []
        this.$message.error((res && res.data && res.data.msg) || '加载角色失败')
      }
    } catch (e) {
      this.roleList = []
      this.$message.error('加载角色失败：' + (e as Error).message + '（请确认已执行 admin-role-custom.sql）')
    } finally {
      this.listLoading = false
    }
  }

  private openCreate() {
    this.createForm = { name: '', code: '', remark: '' }
    this.createVisible = true
    this.$nextTick(() => {
      const form = this.$refs.createForm as any
      if (form && form.clearValidate) form.clearValidate()
    })
  }

  private onCodeInput(val: string) {
    this.createForm.code = String(val || '')
      .toUpperCase()
      .replace(/[^A-Z0-9_]/g, '')
  }

  private submitCreate() {
    const form = this.$refs.createForm as any
    if (!form) return
    form.validate(async (ok: boolean) => {
      if (!ok) return
      this.creating = true
      try {
        const res: any = await createRole({
          name: this.createForm.name.trim(),
          code: this.createForm.code.trim(),
          remark: this.createForm.remark || undefined
        })
        if (String(res.data.code) === '1') {
          this.$message.success('已新增角色，请继续「授权」勾选权限')
          this.createVisible = false
          await this.refreshList()
          const created = res.data.data
          if (created) {
            const row: RoleRow = {
              id: created.id,
              role: created.roleValue,
              name: created.name,
              code: created.code,
              desc: created.remark || '',
              systemFlag: false
            }
            this.openAuth(row)
          }
        } else {
          this.$message.error(res.data.msg || '新增失败')
        }
      } catch (e) {
        this.$message.error('新增失败：' + (e as Error).message)
      } finally {
        this.creating = false
      }
    })
  }

  private handleDelete(row: RoleRow) {
    if (!row.id) return
    this.$confirm(`确定删除角色「${row.name}」？删除后不可恢复。`, '提示', {
      type: 'warning'
    })
      .then(async () => {
        try {
          const res: any = await deleteRole(row.id as number)
          if (String(res.data.code) === '1') {
            this.$message.success('已删除')
            this.refreshList()
          } else {
            this.$message.error(res.data.msg || '删除失败')
          }
        } catch (e) {
          this.$message.error('删除失败：' + (e as Error).message)
        }
      })
      .catch(() => undefined)
  }

  private async ensureTree() {
    if (this.treeLoaded && this.treeData.length) return
    try {
      const listRes: any = await listPermissions()
      if (String(listRes.data.code) !== '1') {
        this.$message.error(listRes.data.msg || '加载权限失败')
        return
      }
      this.treeData = this.buildTree((listRes.data.data || []) as any[])
      this.rebuildTreeIndex()
      this.treeLoaded = true
    } catch (e) {
      this.$message.error('加载权限失败：' + (e as Error).message)
    }
  }

  private rebuildTreeIndex() {
    this.leafIdSet = new Set()
    this.parentOf = {}
    const walk = (nodes: any[], parentId: number) => {
      ;(nodes || []).forEach((n) => {
        if (!n || n.id == null) return
        const id = Number(n.id)
        if (parentId) this.parentOf[id] = parentId
        if (n.children && n.children.length) {
          walk(n.children, id)
        } else {
          this.leafIdSet.add(id)
        }
      })
    }
    walk(this.treeData, 0)
  }

  /** 勾选叶子时一并带上祖先（菜单码如 store:list / employee:list） */
  private collectWithAncestors(leafIds: number[]): number[] {
    const set = new Set<number>()
    ;(leafIds || []).forEach((raw) => {
      let id = Number(raw)
      if (!Number.isFinite(id)) return
      set.add(id)
      let p = this.parentOf[id]
      while (p) {
        set.add(p)
        p = this.parentOf[p]
      }
    })
    return Array.from(set)
  }

  private async openAuth(row: RoleRow, forceReadonly = false) {
    this.currentRole = row
    this.authReadonly = forceReadonly || !this.canAuthorize(row)
    this.dialogVisible = true
    this.authLoading = true
    try {
      await this.ensureTree()
      const roleRes: any = await getRolePermissions(row.role)
      const ids =
        (roleRes &&
          roleRes.data &&
          roleRes.data.data &&
          roleRes.data.data.permissionIds) ||
        []
      // 等树挂载（:key 重建）后再勾选；只勾叶子，父级由树自动半选/全选
      await this.$nextTick()
      await this.$nextTick()
      const tree: any = this.$refs.tree
      if (tree) {
        const leafIds = (ids as any[])
          .map((x) => Number(x))
          .filter((id) => this.leafIdSet.has(id))
        tree.setCheckedKeys([])
        tree.setCheckedKeys(leafIds)
        this.setTreeDisabled(this.authReadonly)
      }
    } catch (e) {
      this.$message.error('加载角色权限失败：' + (e as Error).message)
    } finally {
      this.authLoading = false
    }
  }

  private setTreeDisabled(disabled: boolean) {
    const walk = (nodes: any[]) => {
      nodes.forEach((n) => {
        this.$set(n, 'disabled', disabled)
        if (n.children && n.children.length) walk(n.children)
      })
    }
    walk(this.treeData)
  }

  private onDialogClosed() {
    this.setTreeDisabled(false)
    this.currentRole = null
    this.authReadonly = false
  }

  private buildTree(list: any[]) {
    const map: Record<number, any> = {}
    const roots: any[] = []
    list.forEach((item) => {
      map[item.id] = { ...item, children: [], disabled: false }
    })
    list.forEach((item) => {
      const node = map[item.id]
      const pid = item.parentId || 0
      if (pid && map[pid]) {
        map[pid].children.push(node)
      } else {
        roots.push(node)
      }
    })
    return roots
  }

  private async saveAuth() {
    if (!this.currentRole || this.authReadonly) return
    if (!this.canAuthorize(this.currentRole)) {
      this.$message.warning('无权保存该角色权限')
      return
    }
    const tree: any = this.$refs.tree
    if (!tree) return
    // 只取叶子勾选，再补祖先菜单码；取消某个子权限不会丢掉 store:list 等入口
    const leaves: number[] = (tree.getCheckedKeys(true) || []).map((x: any) =>
      Number(x)
    )
    const permissionIds = this.collectWithAncestors(leaves)
    this.saving = true
    try {
      const res: any = await updateRolePermissions(
        this.currentRole.role,
        permissionIds
      )
      if (String(res.data.code) === '1') {
        this.$message.success(
          `已保存「${this.currentRole.name}」权限，对应用户需重新登录后生效`
        )
        this.dialogVisible = false
      } else {
        this.$message.error(res.data.msg || '保存失败')
      }
    } catch (e) {
      this.$message.error('保存失败：' + (e as Error).message)
    } finally {
      this.saving = false
    }
  }
}
